import { ListIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/menu";
import { RESPONSIVE_CONTROL_HIT_AREA_CLASS } from "~/lib/touchTarget";
import type { TimelineMinimapItem } from "./timelineMinimapItems";
import { TurnStep } from "./TimelineTurnStep";
import { TimelineTurnRail } from "./TimelineTurnRail";

export interface TimelineTurnNavigationProps {
  readonly items: readonly TimelineMinimapItem[];
  readonly currentId: string | null;
  readonly viewportWidth: number;
  readonly onSelect: (item: TimelineMinimapItem) => void;
}

export function TimelineTurnNavigation(props: TimelineTurnNavigationProps) {
  if (props.items.length < 2) return null;
  const currentIndex = Math.max(
    0,
    props.items.findIndex((item) => item.id === props.currentId),
  );
  // Upstream reserves 48px of gutter for the rail. Keep it off the text column.
  const hasGutter = (props.viewportWidth - 768) / 2 >= 48;
  if (hasGutter) return <TimelineTurnRail {...props} currentIndex={currentIndex} />;
  return (
    <nav
      aria-label="Conversation turns"
      className="flex shrink-0 items-center justify-end gap-2 px-3"
    >
      <TurnStep
        direction="previous"
        items={props.items}
        currentIndex={currentIndex}
        onSelect={props.onSelect}
      />
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label="Choose a turn"
          className={`${RESPONSIVE_CONTROL_HIT_AREA_CLASS} inline-flex items-center gap-2 rounded-md px-2 text-muted-foreground text-sm hover:bg-muted`}
        >
          <ListIcon aria-hidden="true" className="size-4" />
          <span>
            Turn {currentIndex + 1} of {props.items.length}
          </span>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="max-h-[60dvh] w-72 max-w-[calc(100vw-1.5rem)] overflow-y-auto"
        >
          {props.items.map((item, index) => (
            <DropdownMenuItem
              key={item.id}
              onClick={() => props.onSelect(item)}
              className="min-h-touch md:min-h-7"
            >
              <span className="truncate">
                {index + 1}. {item.userText?.trim() || "User message"}
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <TurnStep
        direction="next"
        items={props.items}
        currentIndex={currentIndex}
        onSelect={props.onSelect}
      />
    </nav>
  );
}
